import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useContactInfo } from '../hooks/useContactInfo';
import { useSiteColors } from '../hooks/useSiteColors';
import { supabase } from '../lib/supabase';

export const Footer = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { contactInfo, socialLinks } = useContactInfo();
  const { colors } = useSiteColors();
  const [logo, setLogo] = useState({
    url: '',
    alt_ar: '',
    alt_en: '',
  });
  const [footerSettings, setFooterSettings] = useState<any>(null);

  useEffect(() => {
    loadFooterSettings();
  }, [i18n.language]);

  const loadFooterSettings = async () => {
    try {
      const [logoRes, footerRes] = await Promise.all([
        supabase
          .from('site_settings')
          .select('*')
          .eq('setting_key', 'site_logo')
          .maybeSingle(),
        supabase
          .from('site_settings')
          .select('*')
          .eq('setting_key', 'footer_settings')
          .maybeSingle(),
      ]);

      if (logoRes.data?.setting_value) {
        setLogo(logoRes.data.setting_value);
      }

      if (footerRes.data?.setting_value) {
        setFooterSettings(footerRes.data.setting_value);
      }
    } catch (error) {
      console.error('Error loading footer settings:', error);
    }
  };

  const quickLinks = footerSettings?.quick_links || [
    { path: '/', label_ar: 'الرئيسية', label_en: 'Home' },
    { path: '/about', label_ar: 'من نحن', label_en: 'About' },
    { path: '/services', label_ar: 'الخدمات', label_en: 'Services' },
    { path: '/team', label_ar: 'الفريق', label_en: 'Team' },
    { path: '/faq', label_ar: 'الأسئلة الشائعة', label_en: 'FAQ' },
    { path: '/blog', label_ar: 'المدونة', label_en: 'Blog' },
  ];

  const services = [
    t('services.criminal.title'),
    t('services.civil.title'),
    t('services.labor.title'),
    t('services.family.title'),
  ];

  const socialItems = [
    { key: 'facebook', icon: 'ri-facebook-fill', href: socialLinks.facebook },
    { key: 'twitter', icon: 'ri-twitter-x-fill', href: socialLinks.twitter },
    { key: 'instagram', icon: 'ri-instagram-fill', href: socialLinks.instagram },
    { key: 'linkedin', icon: 'ri-linkedin-fill', href: socialLinks.linkedin },
  ];

  const footerDescription = footerSettings
    ? (isRTL ? footerSettings.description_ar : footerSettings.description_en)
    : t('footer.description');

  const copyrightText = footerSettings
    ? (isRTL ? footerSettings.copyright_ar : footerSettings.copyright_en)
    : `© 2024 ${t('hero.title')}. ${t('footer.rights')}`;

  return (
    <footer className="font-cairo relative overflow-hidden" style={{ backgroundColor: colors.primary_dark }}>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: colors.primary }}></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: colors.primary }}></div>
      </div>

      <div className="container mx-auto px-4 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* About */}
          <div className="animate-fade-in">
            <div className="flex items-center gap-3 mb-6">
              <img
                src={logo.url}
                alt={isRTL ? logo.alt_ar : logo.alt_en}
                className="h-16 w-auto object-contain bg-white rounded-lg p-2 transition-transform duration-300 hover:scale-105"
              />
            </div>
            <p className="text-white/70 leading-relaxed mb-6 text-sm">{footerDescription}</p>
            <div className="flex gap-3">
              {socialItems.map((social) => (
                <a
                  key={social.key}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center transition-all duration-300 cursor-pointer hover:scale-110 hover:-translate-y-1"
                  style={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  }}
                >
                  <i className={`${social.icon} text-lg text-white`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="animate-fade-in" style={{ animationDelay: '0.1s' }}>
            <h4 className="text-lg font-bold mb-6 text-white">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3">
              {quickLinks.map((link: any) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/70 hover:text-white hover:translate-x-2 transition-all duration-300 flex items-center gap-2 cursor-pointer text-sm group"
                  >
                    <i
                      className={`ri-arrow-${isRTL ? 'left' : 'right'}-s-line group-hover:scale-125 transition-transform duration-300`}
                      style={{ color: colors.primary_light }}
                    ></i>
                    <span className="transition-colors duration-300" 
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = colors.primary_light;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '';
                      }}
                    >
                      {isRTL ? link.label_ar : link.label_en}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <h4 className="text-lg font-bold mb-6 text-white">{t('footer.services')}</h4>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <Link
                    to="/services"
                    className="text-white/70 hover:text-white hover:translate-x-2 transition-all duration-300 flex items-center gap-2 cursor-pointer text-sm group"
                  >
                    <i
                      className={`ri-arrow-${isRTL ? 'left' : 'right'}-s-line group-hover:scale-125 transition-transform duration-300`}
                      style={{ color: colors.primary_light }}
                    ></i>
                    <span className="transition-colors duration-300"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = colors.primary_light;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '';
                      }}
                    >
                      {service}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="animate-fade-in" style={{ animationDelay: '0.3s' }}>
            <h4 className="text-lg font-bold mb-6 text-white">{t('contact.info')}</h4>
            <ul className="space-y-4">
              {/* أرقام الهواتف */}
              {contactInfo?.phones && contactInfo.phones.length > 0 && contactInfo.phones.map((phone, index) => (
                <li key={`phone-${index}`} className="flex items-center gap-3 group cursor-pointer">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                    style={{ backgroundColor: `${colors.primary}33` }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = colors.primary;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = `${colors.primary}33`;
                    }}
                  >
                    <i className="ri-phone-line transition-colors duration-300" style={{ color: colors.primary_light }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = colors.primary_light;
                      }}
                    ></i>
                  </div>
                  <span
                    className="text-white/80 text-sm group-hover:text-white transition-colors duration-300"
                    dir="ltr"
                  >
                    {phone}
                  </span>
                </li>
              ))}
              
              <li className="flex items-center gap-3 group cursor-pointer">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                  style={{ backgroundColor: `${colors.primary}33` }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = `${colors.primary}33`;
                  }}
                >
                  <i className="ri-mail-line transition-colors duration-300" style={{ color: colors.primary_light }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = colors.primary_light;
                    }}
                  ></i>
                </div>
                <span
                  className="text-white/80 text-sm group-hover:text-white transition-colors duration-300"
                  dir="ltr"
                >
                  {contactInfo?.email ?? 'N/A'}
                </span>
              </li>
              
              {/* العناوين */}
              {contactInfo?.addresses_ar && contactInfo.addresses_ar.length > 0 && contactInfo.addresses_ar.map((address, index) => {
                const addressEn = contactInfo.addresses_en?.[index] || address;
                return (
                  <li key={`address-${index}`} className="flex items-center gap-3 group cursor-pointer">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                      style={{ backgroundColor: `${colors.primary}33` }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = colors.primary;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = `${colors.primary}33`;
                      }}
                    >
                      <i className="ri-map-pin-line transition-colors duration-300" style={{ color: colors.primary_light }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = colors.primary_light;
                        }}
                      ></i>
                    </div>
                    <span className="text-white/80 text-sm group-hover:text-white transition-colors duration-300">
                      {isRTL ? address : addressEn}
                    </span>
                  </li>
                );
              })}
              
              <li className="flex items-center gap-3 group cursor-pointer">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                  style={{ backgroundColor: `${colors.primary}33` }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = `${colors.primary}33`;
                  }}
                >
                  <i className="ri-time-line transition-colors duration-300" style={{ color: colors.primary_light }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = colors.primary_light;
                    }}
                  ></i>
                </div>
                <span className="text-white/80 text-sm group-hover:text-white transition-colors duration-300">
                  {isRTL ? contactInfo?.working_hours_ar : contactInfo?.working_hours_en}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/50 text-sm">
              {copyrightText}
            </p>
            <div className="flex gap-6 text-sm">
              <a
                href="#"
                className="text-white/50 transition-colors duration-300 cursor-pointer"
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = colors.primary_light;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '';
                }}
              >
                {t('footer.privacy')}
              </a>
              <a
                href="#"
                className="text-white/50 transition-colors duration-300 cursor-pointer"
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = colors.primary_light;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '';
                }}
              >
                {t('footer.terms')}
              </a>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/50 transition-colors duration-300 cursor-pointer"
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = colors.primary_light;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '';
                }}
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
